import { LanguageType, TransportModes } from '@nx-smartmonkey/shared/domain';
import { CountryCode } from 'libphonenumber-js';
import moment from 'moment-timezone';
import queryString from 'query-string';

import { SocialLoginProviders } from '../pages/auth/context/reducer';
import { OTP_METHOD, sessionRepository } from '../repositories/session';
import { LoginProvider, SessionRepository } from '../repositories/session/SessionRepository';
import { cookies, postHogService } from '../services';
import { CookiesInterface } from '../services/cookies/cookies';
import { PostHogInterface } from '../services/postHog';
import { Platform } from './CapacitorService';

export interface CreateRegisterTokenProps {
  providerToken: string;
  provider: SocialLoginProviders;
  accessToken: string;
  organizationName: string;
  workSchedule: [number, number];
  language: LanguageType;
  vehicleType: TransportModes;
  referralId?: string;
}

export class SessionService {
  constructor(
    private sessionRepository: SessionRepository,
    private cookies: CookiesInterface,
    private postHogService: PostHogInterface
  ) {}

  async isLogged(): Promise<boolean> {
    return await this.sessionRepository.isLogged();
  }

  loginProvider({
    provider,
    providerToken,
    accessToken,
  }: {
    provider: LoginProvider;
    providerToken: string;
    accessToken: string;
  }): Promise<string> {
    return this.sessionRepository.loginProvider({
      provider,
      providerToken,
      accessToken,
    });
  }

  generateOtpCode({
    phone,
    otpMethod,
    tokenValidator,

    platform,
  }: {
    phone: string;
    otpMethod: OTP_METHOD;
    tokenValidator: {
      data: string;
      signature: string;
    };

    platform: Platform;
  }): Promise<string> {
    return this.sessionRepository.generateOtpCode({ phone, otpMethod, tokenValidator, platform });
  }

  verifyLogin({ otpCode }: { otpCode: string }): Promise<string> {
    return this.sessionRepository.verifyLogin({ otpCode });
  }

  async createRegisterToken({
    providerToken,
    provider,
    accessToken,
    organizationName,
    workSchedule,
    language,
    vehicleType,
    referralId,
  }: CreateRegisterTokenProps): Promise<string> {
    const timeZone = moment.tz.guess();

    const registerUtmInfo = {
      utmCampaign: ``,
      utmContent: ``,
      utmSource: ``,
      utmTerm: ``,
      utmMedium: ``,
      utmId: ``,
      utmGclid: ``,
    };
    const gtmCampaignCookie = await this.cookies.getCookie(`__gtm_campaign_url`);
    if (gtmCampaignCookie) {
      const utmUrl = decodeURIComponent(gtmCampaignCookie);
      const { query } = queryString.parseUrl(utmUrl);
      registerUtmInfo.utmCampaign = (query[`utm_campaign`] ?? ``) as string;
      registerUtmInfo.utmContent = (query[`utm_content`] ?? ``) as string;
      registerUtmInfo.utmSource = (query[`utm_source`] ?? ``) as string;
      registerUtmInfo.utmTerm = (query[`utm_term`] ?? ``) as string;
      registerUtmInfo.utmMedium = (query[`utm_medium`] ?? ``) as string;
      registerUtmInfo.utmId = (query[`utm_id`] ?? ``) as string;
      registerUtmInfo.utmGclid = (query[`utm_gclid`] ?? ``) as string;
    }

    return this.sessionRepository.createRegisterToken({
      providerToken,
      provider,
      accessToken,
      organizationName,
      workSchedule,
      language,
      vehicleType,
      referralId,
      timeZone: timeZone ?? `Europe/Madrid`,
      utmCampaign: registerUtmInfo.utmCampaign,
      utmContent: registerUtmInfo.utmContent,
      utmSource: registerUtmInfo.utmSource,
      utmTerm: registerUtmInfo.utmTerm,
      utmMedium: registerUtmInfo.utmMedium,
      utmId: registerUtmInfo.utmId,
      utmGclid: registerUtmInfo.utmGclid,
    });
  }

  createSessionMagicLink({ vehicleId, routeId }: { vehicleId: string; routeId: string }): Promise<string> {
    return this.sessionRepository.createSessionMagicLink({ vehicleId, routeId });
  }

  deleteSessionToken(): Promise<void> {
    this.postHogService.reset();
    return this.sessionRepository.deleteSessionToken();
  }

  async getToken(): Promise<string | undefined> {
    return await this.sessionRepository.getToken();
  }

  async getCountry(): Promise<CountryCode> {
    const countryCode = await this.sessionRepository.getCountry();
    return countryCode;
  }
}

export const sessionService = new SessionService(sessionRepository, cookies, postHogService);
